import React, { useEffect, useState, useMemo } from 'react'
import Pagecontainer from './Pagecontainer'
import Breadcrumb from './components/Breadcrumb'
import useDocumentTitle from '../functions/useDocumentTitle'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import { Link } from 'react-router-dom'
import { db } from '../firebase'

const NewsPage = () => {
    useDocumentTitle('News - Aabaddha SACCOS Ltd.')

    const [news, setNews] = useState([])
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(9)

    useEffect(() => {
        try {
            const q = query(collection(db, "news"), orderBy("timestamp", "desc"))
            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                const data = []
                querySnapshot.forEach((doc) => {
                    data.push({
                        id: doc.id,
                        ...doc.data()
                    })
                })
                setNews(data)
                // Reset to page 1 when data changes
                setCurrentPage(1)
            })
            return () => unsubscribe()
        } catch (e) {
            console.error('Error fetching news:', e)
        }
    }, [])

    // Calculate pagination
    const totalPages = Math.ceil(news.length / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentNews = useMemo(() => {
        return news.slice(startIndex, endIndex)
    }, [news, startIndex, endIndex])

    // Handle page change
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page)
            window.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pages = []
        const maxVisiblePages = 5

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total pages is less than max visible
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i)
            }
        } else {
            // Show pages with ellipsis
            if (currentPage <= 3) {
                // Show first pages
                for (let i = 1; i <= 4; i++) {
                    pages.push(i)
                }
                pages.push('ellipsis')
                pages.push(totalPages)
            } else if (currentPage >= totalPages - 2) {
                // Show last pages
                pages.push(1)
                pages.push('ellipsis')
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i)
                }
            } else {
                // Show middle pages
                pages.push(1)
                pages.push('ellipsis')
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i)
                }
                pages.push('ellipsis')
                pages.push(totalPages)
            }
        }
        return pages
    }

    return (
        <>
            <Breadcrumb root='Home' current='News' />
            <Pagecontainer>
                <div className='flex flex-col items-center justify-center mt-10 mb-12'>
                    <h3 className='text-primary font-sans text-2xl md:text-3xl font-bold uppercase tracking-wide'>
                        News
                    </h3>
                    <p className='font-vesper text-base font-medium text-red-700 mt-2'>
                        समाचारहरु
                    </p>
                    <div className='w-[30px] bg-primary h-[4px] my-2' />
                </div>

                {news.length > 0 ? (
                    <>
                        {/* Items per page selector */}
                        <div className='flex justify-between items-center mb-6 px-5'>
                            <div className='flex items-center gap-2'>
                                <label htmlFor='itemsPerPage' className='text-sm text-gray-600 font-medium'>
                                    Show:
                                </label>
                                <select
                                    id='itemsPerPage'
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value))
                                        setCurrentPage(1)
                                    }}
                                    className='border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:border-primary'
                                >
                                    <option value={6}>6</option>
                                    <option value={9}>9</option>
                                    <option value={12}>12</option>
                                    <option value={18}>18</option>
                                    <option value={24}>24</option>
                                </select>
                                <span className='text-sm text-gray-600'>
                                    per page
                                </span>
                            </div>
                            <div className='text-sm text-gray-600'>
                                Showing {startIndex + 1}-{Math.min(endIndex, news.length)} of {news.length} news
                            </div>
                        </div>

                        {/* News Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 px-5 mb-10">
                            {currentNews.map((newsItem) => (
                                <NewsCard
                                    key={newsItem.id}
                                    id={newsItem.id}
                                    title={newsItem.title}
                                    content={newsItem.content}
                                    image={newsItem.image}
                                    date={newsItem.date}
                                />
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className='flex flex-col items-center gap-4 mb-10'>
                                <div className='flex items-center gap-2 flex-wrap justify-center'>
                                    {/* Previous Button */}
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className={`px-4 py-2 border transition-all duration-200 ${
                                            currentPage === 1
                                                ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                                : 'border-gray-300 text-gray-700 hover:bg-primary hover:text-white hover:border-primary'
                                        }`}
                                        aria-label='Previous page'
                                    >
                                        <i className="bi bi-chevron-left"></i>
                                    </button>

                                    {/* Page Numbers */}
                                    {getPageNumbers().map((page, index) => {
                                        if (page === 'ellipsis') {
                                            return (
                                                <span key={`ellipsis-${index}`} className='px-2 text-gray-400'>
                                                    ...
                                                </span>
                                            )
                                        }
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => handlePageChange(page)}
                                                className={`px-4 py-2 border transition-all duration-200 ${
                                                    currentPage === page
                                                        ? 'bg-primary text-white border-primary'
                                                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                                }`}
                                                aria-label={`Go to page ${page}`}
                                                aria-current={currentPage === page ? 'page' : undefined}
                                            >
                                                {page}
                                            </button>
                                        )
                                    })}

                                    {/* Next Button */}
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className={`px-4 py-2 border transition-all duration-200 ${
                                            currentPage === totalPages
                                                ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                                : 'border-gray-300 text-gray-700 hover:bg-primary hover:text-white hover:border-primary'
                                        }`}
                                        aria-label='Next page'
                                    >
                                        <i className="bi bi-chevron-right"></i>
                                    </button>
                                </div>

                                {/* Page Info */}
                                <div className='text-sm text-gray-600'>
                                    Page {currentPage} of {totalPages}
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className='flex flex-col items-center justify-center py-20'>
                        <div className='text-gray-400 mb-4'>
                            <i className="bi bi-newspaper text-6xl"></i>
                        </div>
                        <h3 className='text-gray-600 text-lg font-medium'>
                            No news found.
                        </h3>
                    </div>
                )}
            </Pagecontainer>
        </>
    )
}

const NewsCard = ({ id, title, content, image, date }) => {
    const truncatedTitle = title && title.length > 60 ? title.substring(0, 60) + '...' : title
    const truncatedContent = content && content.length > 120 ? content.substring(0, 120) + '...' : content

    return (
        <Link
            to={`/news/${id}`}
            className="group block h-full"
        >
            <div className="relative h-full bg-white border overflow-hidden flex flex-col">
                {/* Image Container */}
                <div className="relative h-48 md:h-56 overflow-hidden bg-gray-100">
                    <img
                        src={image}
                        alt={title || 'News image'}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    {/* Date Badge */}
                    {date && (
                        <div className="absolute top-4 left-4 bg-primary text-white px-3 py-1 text-xs font-medium">
                            {date}
                        </div>
                    )}
                </div>

                {/* Content Container */}
                <div className="flex-1 flex flex-col p-5 md:p-6">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors duration-300 font-kalimati">
                        {truncatedTitle}
                    </h3>
                    <p className="text-gray-600 text-sm md:text-base mb-4 flex-1 overflow-hidden font-vesper">
                        {truncatedContent}
                    </p>

                    {/* Read More Link */}
                    <div className="flex items-center gap-2 text-primary font-medium text-sm mt-auto pt-3 border-t border-gray-100 group-hover:border-primary/30 transition-colors duration-300">
                        <span>Read More</span>
                        <i className="bi bi-arrow-right-circle group-hover:translate-x-1 transition-transform duration-300"></i>
                    </div>
                </div>
            </div>
        </Link>
    )
}

export default NewsPage